import { ScrollingModule } from '@angular/cdk/scrolling';
import { Component, inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  FwAvatarModule,
  FwButtonModule,
  FwButtonToggleModule,
  FwCardModule,
  FwContainedInputModule,
  FwIconButtonModule,
  FwIconModule,
  FwLayoutsModule,
  FwMenuModule,
  FwSectionHeadingModule,
  FwSelectMenuModule,
  FwSwitchModule,
  FwTextInputModule,
} from '@flywheel-io/vision';
import flatten from 'lodash/flatten';
import sortBy from 'lodash/sortBy';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, filter, scan, startWith, switchMap } from 'rxjs/operators';

import { AdminModule } from 'app/src/admin/admin.module';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { SessionModule } from 'app/src/sessions/session.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';

import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { stringToHslColor } from '../../shared/color.utils';
import { CatalogProject } from '../../shared/models/project.model';
import { replayValue } from '../../shared/observable.utils';
import { getInitials } from '../../shared/string.utils';

interface CatalogProjectDisplay extends CatalogProject {
  avatarColor: string;
  avatarInitials: string;
  chips: string[];
  readMore: boolean;
  truncatedSummary: string;
}

@Component({
  host: {
    'test-id': 'project-catalog',
  },
  selector: 'project-catalog',
  templateUrl: './project-catalog.component.html',
  styleUrls: ['./project-catalog.component.scss'],
  imports: [
    AdminModule,
    ContainerModule,
    DirectivesModule,
    FileModule,
    FormsModule,
    FwAvatarModule,
    FwButtonModule,
    FwButtonToggleModule,
    FwCardModule,
    FwContainedInputModule,
    FwIconButtonModule,
    FwIconModule,
    FwLayoutsModule,
    FwMenuModule,
    FwSectionHeadingModule,
    FwSelectMenuModule,
    FwSwitchModule,
    FwTextInputModule,
    MaterialModule,
    PipesModule,
    ReactiveFormsModule,
    ScrollingModule,
    SessionModule,
    SharedModule,
    RouterModule,
  ],
})
export class ProjectCatalogComponent implements OnDestroy {
  private coreApi = inject(CoreApiService);
  private logger = inject(LoggingService);

  readonly form = new FormGroup({
    ageFilters: new FormControl(new Set([])),
    bodyRegions: new FormControl(new Set([])),
    fileTypeFilters: new FormControl(new Set([])),
    measurementAndScanTags: new FormControl(new Set([])),
    modalities: new FormControl(new Set([])),
    organSystems: new FormControl(new Set([])),
    projectTags: new FormControl(new Set([])),
    searchString: new FormControl(''),
    therapeuticAreas: new FormControl(new Set([])),
  });
  ageFilters: string[] = [];
  bodyRegionFilters: string[] = [];
  fileTypeFilters: string[] = [];
  readonly loadMore$ = new Subject<void>();
  measurementFilters: string[] = [];
  modalityFilters: string[] = [];
  organSystemFilters: string[] = [];
  projectList: CatalogProjectDisplay[] = [];
  projectTags: string[] = [];
  therapeuticAreaFilters: string[] = [];

  private subscriptions: Record<string, Subscription> = {};

  constructor() {
    this.coreApi.projects.catalogListFilterOptions.get().subscribe((sharingFilterOptions) => {
      this.ageFilters = sortAlpha(sharingFilterOptions.age_groups ?? []);
      this.bodyRegionFilters = sortAlpha(sharingFilterOptions.body_regions ?? []);
      this.fileTypeFilters = sortAlpha(sharingFilterOptions.file_types ?? []);
      this.measurementFilters = sortAlpha(sharingFilterOptions.measurements ?? []);
      this.modalityFilters = sortAlpha(sharingFilterOptions.modalities ?? []);
      this.organSystemFilters = sortAlpha(sharingFilterOptions.organ_systems ?? []);
      this.therapeuticAreaFilters = sortAlpha(sharingFilterOptions.therapeutic_areas ?? []);
    });

    replayValue(this.form, { debounce: 200 })
      .pipe(
        switchMap(
          ({
            ageFilters,
            bodyRegions,
            fileTypeFilters,
            measurementAndScanTags,
            modalities,
            organSystems,
            projectTags,
            searchString,
            therapeuticAreas,
          }) => {
            const filters = [];
            if (projectTags.size > 0) {
              filters.push(...Array.from(projectTags).map((tag) => `project.tags.raw=${tag}`));
            }
            if (fileTypeFilters.size > 0) {
              filters.push(...Array.from(fileTypeFilters).map((area) => `project.stats.file_types.raw=${area}`));
            }
            if (therapeuticAreas.size > 0) {
              filters.push(...Array.from(therapeuticAreas).map((area) => `project.settings.sharing.therapeutic_areas.raw=${area}`));
            }
            if (bodyRegions.size > 0) {
              filters.push(...Array.from(bodyRegions).map((region) => `project.settings.sharing.body_regions.raw=${region}`));
            }
            if (organSystems.size > 0) {
              filters.push(...Array.from(organSystems).map((system) => `project.settings.sharing.organ_systems.raw=${system}`));
            }
            if (ageFilters.size > 0) {
              filters.push(...Array.from(ageFilters).map((age) => `project.stats.age_groups.raw=${age}`));
            }
            if (modalities.size > 0) {
              filters.push(...Array.from(modalities).map((modality) => `project.stats.modalities.raw=${modality}`));
            }
            if (measurementAndScanTags.size > 0) {
              filters.push(...Array.from(measurementAndScanTags).map((measurement) => `project.stats.measurements.raw=${measurement}`));
            }
            const queryParams = {
              search_string: searchString,
              filter: filters.join(','),
              limit: 10,
              skip: 0,
            };
            return this.loadMore$.pipe(
              filter(() => Boolean(queryParams.skip)),
              startWith(undefined as void),
              switchMap(() =>
                this.coreApi.projects.catalogList.get(queryParams).pipe(
                  catchError((error) => {
                    this.logger.error(error);
                    return of({ count: 0, total: 0, results: [] as CatalogProject[] });
                  }),
                ),
              ),
              scan((allProjects, newPage) => {
                const combined = allProjects.concat(newPage.results);
                if (combined.length < newPage.total && newPage.results.length > 0) {
                  queryParams.skip = combined.length;
                } else {
                  queryParams.skip = null;
                }
                return combined;
              }, [] as CatalogProject[]),
            );
          },
        ),
      )
      .subscribe({
        next: (projects) => {
          // we should be getting a possible list of tags from the API, not built from immediate results FLYW-12246
          this.projectTags = sortAlpha(Array.from(new Set([...this.projectTags, ...flatten(projects.map((project) => project.tags))])));
          this.projectList = projects.map((project) => {
            return {
              ...project,
              avatarColor: stringToHslColor(project.label),
              avatarInitials: getInitials(project.label),
              chips: sortAlpha(project.tags),
              readMore: true,
              truncatedSummary: getTruncatedSummary(project.summary),
            };
          });
        },
        error: (error) => {
          this.logger.error(error);
        },
      });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }
}

/**
 * Take the first two substantive lines of summary, max 150 characters.
 */
function getTruncatedSummary(summary: string): string {
  const lines = (summary ?? '').split('\n');
  return lines
    .filter((line) => line)
    .slice(0, 2)
    .join('\n')
    .slice(0, 150);
}

function sortAlpha(options: string[]): string[] {
  return sortBy(options, (option) => (option ?? '').toLowerCase());
}
